import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { generatedContentApi, GeneratedPlan } from 'src/api/generatedContent';
import styles from './CirclePlanForm.module.scss';
import FileUploadField from './FileUploadField';

interface CirclePlanFormProps {
  onPlanGenerated: (plan: GeneratedPlan) => void;
}

const CirclePlanForm: React.FC<CirclePlanFormProps> = ({ onPlanGenerated }) => {
  const [circleDescription, setCircleDescription] = useState('');
  const [contentInspiration, setContentInspiration] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfIds, setPdfIds] = useState<string[]>([]);
  const [challengeCount, setChallengeCount] = useState<number>(10);

  const handleChallengeCountChange = (event: SelectChangeEvent<number>) => {
    setChallengeCount(event.target.value as number);
  };

  const handleGeneratePlan = async () => {
    if (!circleDescription || !contentInspiration) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setIsGenerating(true);

      const params = {
        circleDescription,
        contentInspiration,
        pdfIds: pdfIds.length > 0 ? pdfIds : undefined,
        challengeCount: challengeCount,
      };

      const generatedPlan =
        await generatedContentApi.generateCircleContentPlan(params);

      onPlanGenerated(generatedPlan);
    } catch (error) {
      toast.error('Failed to generate content plan. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <h2>Create Circle Content Plan</h2>

      <div className={styles.formGroup}>
        <TextField
          label="Circle Description"
          variant="outlined"
          fullWidth
          value={circleDescription}
          onChange={(e) => setCircleDescription(e.target.value)}
          placeholder="Describe your Circle and its members. What is the purpose and goal of your circle?"
          required
          multiline
          rows={4}
        />
      </div>

      <div className={styles.formGroup}>
        <TextField
          label="Content Inspiration"
          variant="outlined"
          fullWidth
          value={contentInspiration}
          onChange={(e) => setContentInspiration(e.target.value)}
          placeholder="What tools, techniques, or areas of expertise should influence the posts? What do you think should be relevant to the posts?"
          required
          multiline
          rows={4}
        />
      </div>

      <div className={styles.formGroup}>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="challenge-count-label">
            Number of Challenges
          </InputLabel>
          <Select
            labelId="challenge-count-label"
            id="challenge-count-select"
            value={challengeCount}
            label="Number of Challenges"
            onChange={handleChallengeCountChange}
          >
            <MenuItem value={10}>10 Challenges</MenuItem>
            <MenuItem value={20}>20 Challenges</MenuItem>
            <MenuItem value={30}>30 Challenges</MenuItem>
            <MenuItem value={60}>60 Challenges</MenuItem>
            <MenuItem value={90}>90 Challenges</MenuItem>
            <MenuItem value={120}>120 Challenges</MenuItem>
            <MenuItem value={150}>150 Challenges</MenuItem>
            <MenuItem value={180}>180 Challenges</MenuItem>
          </Select>
        </FormControl>
      </div>

      <div className={styles.formGroup}>
        <FileUploadField
          onFileUploaded={setPdfIds}
          label="Upload PDFs for Additional Context (Optional)"
          maxFiles={3}
        />
      </div>

      <div className={styles.buttonContainer}>
        <button
          type="button"
          className={styles.generateButton}
          onClick={handleGeneratePlan}
          disabled={isGenerating || !circleDescription || !contentInspiration}
        >
          {isGenerating ? (
            <>
              <CircularProgress size={24} color="inherit" />
              <span className={styles.loadingText}>Generating Plan...</span>
            </>
          ) : (
            'Generate Circle Plan'
          )}
        </button>
      </div>
    </div>
  );
};

export default CirclePlanForm;
